import moment from 'moment';

const VOUCHER_TITLES = {
    issue: 'سند الصرف',
    finalInvoice: 'الفاتورة النهائية',
    receipt: 'سند الدخول (استلام)',
};

const VOUCHER_HEADERS = {
    issue: 'وثيقة صرف من المخزن المصدر إلى الجهة المستفيدة',
    finalInvoice: 'فاتورة نهائية للأصناف المصروفة بحسب الأسعار المعتمدة',
    receipt: 'سند دخول إلى مخزن المرفق المستفيد',
};

function escapeHtml(s) {
    if (s == null || s === '') return '';
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function fmtDate(value, withTime = false) {
    if (!value) return '—';
    try {
        return moment(value).format(withTime ? 'YYYY/MM/DD HH:mm' : 'YYYY/MM/DD');
    } catch {
        return '—';
    }
}

function fmtMoney(value) {
    if (value == null || value === '') return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function fmtNumber(value) {
    if (value == null || value === '') return '0';
    const n = Number(value);
    if (!Number.isFinite(n)) return '0';
    return new Intl.NumberFormat('en-US').format(n);
}

function buildLinesRows(detail, docKey) {
    const lines = detail.lines || [];
    const showPrice = docKey === 'finalInvoice';
    return lines.map((line, idx) => {
        const total = line.unitPrice != null ? Number(line.unitPrice) * Number(line.quantity || 0) : null;
        return `<tr class="${idx % 2 === 0 ? 'bg-stripe-1' : 'bg-stripe-2'}">
            <td>${idx + 1}</td>
            <td class="text-end">${escapeHtml(line.productName || '')}</td>
            <td>${escapeHtml(line.serialNumber || '—')}</td>
            <td>${escapeHtml(line.expiryDate ? moment(line.expiryDate).format('YYYY/MM/DD') : '—')}</td>
            <td>${fmtNumber(line.quantity)}</td>
            ${showPrice ? `<td>${fmtMoney(line.unitPrice)}</td><td>${total != null ? fmtMoney(total) : '—'}</td>` : ''}
        </tr>`;
    }).join('');
}

export function buildDispatchDocumentHtml(detail, docKey, options) {
    const opts = options || {};
    const title = VOUCHER_TITLES[docKey] || 'سند';
    const subtitle = VOUCHER_HEADERS[docKey] || '';
    const code = escapeHtml(detail.code || '');
    const docRef = `${title} — ${code}`;
    const logoUrl = opts.logoUrl || '/assets/img/1/logo.png';
    const showPrice = docKey === 'finalInvoice';
    const lines = detail.lines || [];

    let totalAmount = 0;
    let totalQuantity = 0;
    lines.forEach((line) => {
        totalQuantity += Number(line.quantity || 0);
        if (line.unitPrice != null) {
            totalAmount += Number(line.unitPrice) * Number(line.quantity || 0);
        }
    });

    const linesRows = buildLinesRows(detail, docKey);

    const issueDate = detail.issueVoucherPrintedOn ? fmtDate(detail.issueVoucherPrintedOn, true) : '—';
    const finalDate = detail.finalInvoicePrintedOn ? fmtDate(detail.finalInvoicePrintedOn, true) : '—';
    const receiptDate = detail.receiptVoucherPrintedOn ? fmtDate(detail.receiptVoucherPrintedOn, true) : '—';

    const sigBlocks = docKey === 'receipt' ? `
        <div class="col-4">
          <p><strong>أمين مخزن المرفق المستلم</strong><br/>
          الاسم: ............................<br/>
          التوقيع: ............................<br/>
          تاريخ الاستلام: ............................</p>
        </div>
        <div class="col-4">
          <p><strong>مدير المرفق</strong><br/>
          الاسم: ............................<br/>
          التوقيع والختم: ............................</p>
        </div>
        <div class="col-4">
          <p><strong>مراجع الصرف — وزارة الصحة</strong><br/>
          الاسم: ............................<br/>
          التوقيع: ............................</p>
        </div>` : `
        <div class="col-4">
          <p><strong>إعداد السند</strong><br/>
          الاسم: ............................<br/>
          التوقيع: ............................</p>
        </div>
        <div class="col-4">
          <p><strong>أمين المخزن المصدر</strong><br/>
          الاسم: ............................<br/>
          التوقيع والختم: ............................</p>
        </div>
        <div class="col-4">
          <p><strong>المستلم لدى الجهة المستفيدة</strong><br/>
          الاسم: ............................<br/>
          التوقيع: ............................<br/>
          تاريخ الاستلام: ............................</p>
        </div>`;

    return `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(docRef)}</title>
  <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700&display=swap" rel="stylesheet">
  <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.0.2/dist/css/bootstrap.min.css" rel="stylesheet" crossorigin="anonymous" />
  <style>
    body { width:100%; margin:0; padding:0; background:#fafafa; color:#1a1a1a; font:11px "Cairo",sans-serif; -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; }
    * { box-sizing:border-box; }
    .moh-header { border-bottom:3px solid #0d6b4d; padding-bottom:8px; margin-bottom:12px; }
    .moh-brand { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
    .moh-logo { height:52px; width:auto; max-width:140px; object-fit:contain; flex-shrink:0; }
    .moh-title { color:#0d6b4d; font-weight:700; font-size:18px; letter-spacing:0.02em; }
    .moh-sub { font-size:12px; color:#444; margin-top:4px; }
    .doc-title { font-size:15px; font-weight:700; margin:8px 0 4px; }
    .page-container { width:210mm; min-height:297mm; margin:10mm auto; border:1px solid #d3d3d3; border-radius:4px; background:#fff; box-shadow:0 0 5px rgba(0,0,0,.08); }
    .page-inner { padding:10mm; min-height:297mm; }
    th, td { line-height:1.35; font-size:10.5px; vertical-align:middle !important; }
    .bg-gray { background-color:#b8c9c4 !important; color:#111; }
    .bg-stripe-1 { background-color:#eef4f2 !important; }
    .bg-stripe-2 { background-color:#f5f9f7 !important; }
    .info-table tbody th { width:34%; background:#f1f6f4; }
    .totals-row td { font-weight:700; background:#f3f9f6; }
    .signatures p { font-size:10px; line-height:1.5; }
    .print-footer { margin-top:16px; padding-top:10px; border-top:1px solid #ccc; font-size:9px; color:#555; line-height:1.6; }
    .doc-stamp { display:inline-block; padding:4px 10px; border:2px dashed #0d6b4d; color:#0d6b4d; font-weight:700; border-radius:6px; }
    @page { size:A4; margin:0; }
    @media print {
      html, body { width:210mm; height:297mm; background:#fff; }
      .page-container { margin:0; border:none; box-shadow:none; width:100%; min-height:auto; }
    }
  </style>
</head>
<body>
  <div class="page-container">
    <div class="page-inner">
      <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div class="moh-header flex-grow-1">
          <div class="moh-brand">
            <img src="${escapeHtml(logoUrl)}" alt="" class="moh-logo" onerror="this.style.display='none'" />
            <div>
              <div class="moh-title">وزارة الصحة</div>
              <div class="moh-sub">منظومة إدارة المخازن والدواء — وحدة الصرف</div>
            </div>
          </div>
        </div>
        <div class="text-end" style="min-width:160px;">
          <div class="doc-title">${escapeHtml(title)}</div>
          <div class="small text-muted">${escapeHtml(subtitle)}</div>
          <div class="small text-muted">تاريخ الطباعة: ${escapeHtml(moment().format('YYYY/MM/DD HH:mm'))}</div>
        </div>
      </div>

      <div class="row gx-3 mb-2">
        <div class="col-6">
          <table class="table table-sm table-bordered info-table mb-2">
            <tbody>
              <tr><th>رقم السند</th><td><strong>${code}</strong></td></tr>
              <tr><th>نوع الصرف</th><td>${escapeHtml(detail.dispatchSourceTypeLabel || '—')}</td></tr>
              <tr><th>تاريخ الإنشاء</th><td>${escapeHtml(fmtDate(detail.createdOn, true))}</td></tr>
              <tr><th>تاريخ سند الصرف</th><td>${escapeHtml(issueDate)}</td></tr>
              ${docKey === 'finalInvoice' ? `<tr><th>تاريخ الفاتورة</th><td>${escapeHtml(finalDate)}</td></tr>` : ''}
              ${docKey === 'receipt' ? `<tr><th>تاريخ سند الدخول</th><td>${escapeHtml(receiptDate)}</td></tr>` : ''}
            </tbody>
          </table>
        </div>
        <div class="col-6">
          <table class="table table-sm table-bordered info-table mb-2">
            <tbody>
              <tr><th>الجهة المستفيدة</th><td>${escapeHtml(detail.facilityName || '—')}</td></tr>
              <tr><th>المخزن المصدر</th><td>${escapeHtml(detail.sourceStockingName || '—')}</td></tr>
              ${detail.sourceMedicineOrderCode ? `<tr><th>طلب مرتبط</th><td>${escapeHtml(detail.sourceMedicineOrderCode)}</td></tr>` : ''}
              <tr><th>الحالة</th><td><span class="doc-stamp">${escapeHtml(detail.statusLabel || '—')}</span></td></tr>
            </tbody>
          </table>
        </div>
      </div>

      ${detail.note ? `<div class="mb-2"><strong>ملاحظة:</strong> ${escapeHtml(detail.note)}</div>` : ''}

      <table class="table table-sm table-bordered text-center align-middle">
        <thead>
          <tr class="bg-gray">
            <th width="30">#</th>
            <th>الصنف</th>
            <th width="110">التشغيلة</th>
            <th width="100">تاريخ الصلاحية</th>
            <th width="80">الكمية</th>
            ${showPrice ? '<th width="90">سعر الوحدة</th><th width="100">الإجمالي</th>' : ''}
          </tr>
        </thead>
        <tbody>${linesRows || `<tr><td colspan="${showPrice ? 7 : 5}" class="text-center">لا توجد بنود</td></tr>`}</tbody>
        <tfoot>
          <tr class="totals-row">
            <td colspan="4" class="text-end">الإجمالي</td>
            <td>${fmtNumber(totalQuantity)}</td>
            ${showPrice ? `<td>—</td><td>${fmtMoney(totalAmount)}</td>` : ''}
          </tr>
        </tfoot>
      </table>

      <div class="row signatures text-center mt-4">
        ${sigBlocks}
      </div>

      <div class="print-footer">
        <p class="mb-1"><strong>بطاقة المستند:</strong> وثيقة معتمدة في إجراءات وزارة الصحة. تتطلب توقيع وختم الجهات المختصة لاعتمادها.</p>
        <p class="mb-0 small">مرجع المستند: <strong>${code}</strong> — ${escapeHtml(title)} — وزارة الصحة</p>
      </div>
    </div>
  </div>
</body>
</html>`;
}

export function printDispatchDocument(detail, docKey, options) {
    const html = buildDispatchDocumentHtml(detail, docKey, options);
    const w = window.open('', '_blank', 'width=900,height=1200');
    if (!w) return false;
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => {
        try { w.print(); } catch (_) { /* ignore */ }
    }, 350);
    return true;
}
